import { PerspectiveCamera, Vector3 } from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { CameraConfig } from './types';

type Vec3Tuple = [number, number, number];

interface CameraTween {
  startPos: Vector3;
  endPos: Vector3;
  startTarget: Vector3;
  endTarget: Vector3;
  startFov: number;
  endFov: number;
  elapsedMs: number;
  durationMs: number;
}

export class CameraSystem {
  readonly camera: PerspectiveCamera;
  readonly controls: OrbitControls;
  private tween: CameraTween | null = null;

  constructor(config: CameraConfig, canvas: HTMLCanvasElement) {
    const aspect = canvas.clientWidth / canvas.clientHeight;
    this.camera = new PerspectiveCamera(config.fov, aspect, config.near, config.far);
    this.camera.position.set(...config.position);

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.target.set(...config.target);

    const ctrl = config.controls;
    if (ctrl) {
      this.controls.enableDamping = ctrl.enableDamping ?? true;
      this.controls.dampingFactor = ctrl.dampingFactor ?? 0.05;
      if (ctrl.minDistance !== undefined) this.controls.minDistance = ctrl.minDistance;
      if (ctrl.maxDistance !== undefined) this.controls.maxDistance = ctrl.maxDistance;
      this.controls.autoRotate = ctrl.autoRotate ?? false;
      this.controls.autoRotateSpeed = ctrl.autoRotateSpeed ?? 2.0;
      this.controls.enablePan = ctrl.enablePan ?? false;
    }

    this.controls.update();
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  update(): void {
    this.controls.update();
  }

  /// Kick off a programmatic camera tween. Caller passes target position,
  /// target look-at, and an optional FOV. Pass undefined for any field to
  /// keep the current value at that axis.
  animateTo(
    targetPos: Vec3Tuple | undefined,
    targetTarget: Vec3Tuple | undefined,
    targetFov: number | undefined,
    durationMs: number,
  ): void {
    this.tween = {
      startPos: this.camera.position.clone(),
      endPos: targetPos ? new Vector3(...targetPos) : this.camera.position.clone(),
      startTarget: this.controls.target.clone(),
      endTarget: targetTarget ? new Vector3(...targetTarget) : this.controls.target.clone(),
      startFov: this.camera.fov,
      endFov: targetFov ?? this.camera.fov,
      elapsedMs: 0,
      durationMs: Math.max(1, durationMs),
    };
  }

  /// Advance the active tween by `deltaMs`. Returns true if a tween is
  /// active so the caller can skip the normal OrbitControls update path
  /// while the camera is being driven programmatically.
  advanceTween(deltaMs: number): boolean {
    const tween = this.tween;
    if (!tween) return false;
    tween.elapsedMs += deltaMs;
    const t = Math.min(1, tween.elapsedMs / tween.durationMs);
    const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
    this.camera.position.lerpVectors(tween.startPos, tween.endPos, e);
    this.controls.target.lerpVectors(tween.startTarget, tween.endTarget, e);
    if (tween.startFov !== tween.endFov) {
      this.camera.fov = tween.startFov + (tween.endFov - tween.startFov) * e;
      this.camera.updateProjectionMatrix();
    }
    this.controls.update();
    if (t >= 1) {
      this.tween = null;
    }
    return true;
  }

  dispose(): void {
    this.controls.dispose();
  }
}
